/**
 * Portal application root (Frontend Integration).
 *
 * Wires the four Refine providers, the react-router-v6 adapter, the full
 * resource registry (frontend.spec §7 / CONTRACT §5), and every route:
 *   - the authenticated app shell (AppLayout) with the command palette,
 *   - one list/show/create/edit route per CRUD resource (D3/D4/D5/D6),
 *   - read-only list/show routes for events / rule-runs / agent-runs,
 *   - custom pages: /, /dashboard, /operations, /okr-dashboard,
 *     /skills/tree, /processes/:id/graph, /agent-runs/:id/trace,
 *   - the unauthenticated /login page,
 *   - a catch-all 404 rendered inside the shell.
 *
 * The whole tree is wrapped in a top-level ErrorBoundary.
 */
import { Authenticated, ErrorComponent, Refine } from "@refinedev/core";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { Outlet, Route, Routes } from "react-router-dom";

import { dataProvider } from "@/app/providers/data-provider";
import { authProvider } from "@/app/providers/auth-provider";
import { accessControlProvider } from "@/app/providers/access-control-provider";
import { notificationProvider } from "@/app/providers/notification-provider";

import { RESOURCES } from "@/lib/routes";
import { AppLayout } from "@/components/layout";
import { CommandMenu } from "@/components/command-menu";
import { ErrorBoundary } from "@/components/app/ErrorBoundary";

import { Login } from "@/pages/Login";
import { Dashboard } from "@/pages/Dashboard";
import { Operations } from "@/pages/Operations";
import { OKRDashboard } from "@/pages/OKRDashboard";
import { ContextAtlas } from "@/pages/ContextAtlas";
import { PackageGraph } from "@/pages/PackageGraph";
import { EventStream } from "@/pages/EventStream";
import { Docs, ScreenMockupStandalone } from "@/pages/Docs";
import { Permissions } from "@/pages/Permissions";

import { ProjectList, ProjectShow, ProjectCreate, ProjectEdit } from "@/resources/projects";
import { CompanyList, CompanyShow, CompanyCreate, CompanyEdit } from "@/resources/companies";
import { TaskList, TaskShow, TaskCreate, TaskEdit } from "@/resources/tasks";
import { UserList, UserShow, UserCreate, UserEdit } from "@/resources/users";
import { RoleList, RoleShow } from "@/resources/roles";
import { ArtifactList, ArtifactShow, ArtifactCreate, ArtifactEdit } from "@/resources/artifacts";
import { DocumentList, DocumentShow, DocumentCreate, DocumentEdit } from "@/resources/documents";
import { EvidenceList, EvidenceShow, EvidenceCreate, EvidenceEdit } from "@/resources/evidence";
import {
  DecisionList,
  DecisionShow,
  DecisionCreate,
  DecisionEdit,
  DecisionRoomRoute,
} from "@/resources/decisions";
import { ObjectiveList, ObjectiveShow, ObjectiveCreate, ObjectiveEdit } from "@/resources/objectives";
import { KeyResultList, KeyResultShow, KeyResultCreate, KeyResultEdit } from "@/resources/key-results";
import { MetricList, MetricShow, MetricCreate, MetricEdit } from "@/resources/metrics";
import { MeasurementList, MeasurementShow, MeasurementCreate } from "@/resources/measurements";
import { SkillList, SkillShow, SkillCreate, SkillEdit, SkillTree } from "@/resources/skills";
import { ProcessList, ProcessShow, ProcessCreate, ProcessEdit, ProcessGraph } from "@/resources/processes";
import { PhaseList, PhaseShow, PhaseCreate, PhaseEdit } from "@/resources/phases";
import { RuleList, RuleShow, RuleCreate, RuleEdit } from "@/resources/rules";
import { EventList, EventShow } from "@/resources/events";
import { RuleRunList, RuleRunShow } from "@/resources/rule-runs";
import { AgentRunList, AgentRunShow, AgentTrace } from "@/resources/agent-runs";
// Runtime/observability [PROPOSED] teaser modules (frontend-entity-coverage
// §2.3). Discoverable list/show that degrade to a labeled-empty teaser until
// their backing endpoints ship — never a hard error.
import { EventSourceList, EventSourceShow } from "@/resources/event-sources";
import {
  ContourInstanceList,
  ContourInstanceShow,
} from "@/resources/contour-instances";
import {
  TransitionProposalList,
  TransitionProposalShow,
} from "@/resources/transition-proposals";
import { EffectRunList, EffectRunShow } from "@/resources/effect-runs";

// v0.2 (context-centric extension): Context Atlas, Signals, unified Packages,
// type-scoped package facades (bets/frames/principles), Claims, Artifact
// Positions, Timers, Context Bindings, Package Links (CONTRACT_V2 §3.4).
import { ContextList, ContextShow, ContextCreate, ContextEdit } from "@/resources/contexts";
import { ContextDetail } from "@/resources/contexts/ContextDetail";
import { ContextControlRoomRoute } from "@/resources/contexts/ControlRoomPanel";
import { ActionCardList, ActionCardShow } from "@/resources/action-cards";
import {
  ContextBindingList,
  ContextBindingShow,
  ContextBindingCreate,
  ContextBindingEdit,
} from "@/resources/context-bindings";
import { SignalList, SignalShow, SignalCreate, SignalEdit } from "@/resources/signals";
import { SignalTriage } from "@/resources/signals/SignalTriage";
import { PackageList, PackageShow, PackageCreate, PackageEdit } from "@/resources/packages";
import {
  PackageLinkList,
  PackageLinkShow,
  PackageLinkCreate,
} from "@/resources/package-links";
import { BetList, BetShow, BetCreate, BetEdit } from "@/resources/bets";
import { FrameList, FrameShow, FrameCreate, FrameEdit } from "@/resources/frames";
import {
  PrincipleList,
  PrincipleShow,
  PrincipleCreate,
  PrincipleEdit,
} from "@/resources/principles";
import { ClaimList, ClaimShow, ClaimCreate, ClaimEdit } from "@/resources/claims";
import {
  ArtifactPositionList,
  ArtifactPositionShow,
  ArtifactPositionCreate,
  ArtifactPositionEdit,
} from "@/resources/artifact-positions";
import { TimerList, TimerShow, TimerCreate, TimerEdit } from "@/resources/timers";

// S13 (Wave 4a) human surfaces: Approval Center, Runtime Trace Viewer, and
// Context Packet Inspector — custom routes appended in their own block below.
import { ApprovalCenter } from "@/resources/approval-center";
import { TraceViewerRoute } from "@/resources/trace-viewer";
import { PacketInspectorRoute } from "@/resources/packet-inspector";

// S14 (Wave 4b) human surfaces: ten additional read-projection screens. Most
// are id-keyed drill surfaces nested under their owning resource block; the
// list-first ones (Attention Queue, Access Lens, Lens Builder, Package Catalog)
// are top-level. Custom routes — not Refine CRUD resources.
import { ProjectControlTowerPage } from "@/resources/project-control-tower";
import { ContextExplorerRoute } from "@/resources/context-explorer";
import { AttentionQueue } from "@/resources/attention-queue";
import { StrategyMapRoute } from "@/resources/strategy-map";
import { SkillWorkspaceRoute } from "@/resources/skill-workspace";
import { ContourBoardRoute } from "@/resources/contour-board";
import { AccessLensRoute } from "@/resources/access-lens";
import { ActorPassportRoute } from "@/resources/actor-passport";
import { LensBuilderList, LensBuilderRoute } from "@/resources/lens-builder";
import { PackageCatalogPage } from "@/resources/package-catalog";

// S05/S18 (Wave 4c) executive + knowledge surfaces. The Executive Cockpit is the
// founder/top-management portfolio landing (/executive); the Knowledge Assistant
// is the access-filtered retrieval workbench (/knowledge/assistant). Top-level
// custom routes appended below — not Refine CRUD resources.
import { ExecutiveCockpit } from "@/resources/executive-cockpit";
import { KnowledgeAssistant } from "@/resources/knowledge-assistant";

/**
 * Authenticated shell: gates the nested routes behind the auth provider and
 * renders the persistent layout. The command palette lives here so it can use
 * Refine navigation hooks and is mounted exactly once for the session.
 */
function AuthenticatedLayout() {
  return (
    <Authenticated
      key="authenticated-shell"
      fallback={<CatchAllNavigate to="/login" />}
    >
      <AppLayout>
        <Outlet />
        <CommandMenu />
      </AppLayout>
    </Authenticated>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        accessControlProvider={accessControlProvider}
        notificationProvider={notificationProvider}
        routerProvider={routerProvider}
        resources={RESOURCES}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          disableTelemetry: true,
          useNewQueryKeys: true,
        }}
      >
        <Routes>
          <Route
            element={
              <Authenticated
                key="authenticated-mockup"
                fallback={<CatchAllNavigate to="/login" />}
              >
                <Outlet />
              </Authenticated>
            }
          >
            <Route path="/mockups/v3/:slug" element={<ScreenMockupStandalone />} />
          </Route>

          {/* Authenticated application shell. */}
          <Route element={<AuthenticatedLayout />}>
            {/* Home → Dashboard. */}
            <Route index element={<Dashboard />} />

            {/* Custom pages. */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/operations" element={<Operations />} />
            <Route path="/okr-dashboard" element={<OKRDashboard />} />
            <Route path="/skills/tree" element={<SkillTree />} />
            <Route path="/processes/:id/graph" element={<ProcessGraph />} />

            {/* Documentation reader (FRONTEND-FOLLOWUP §C). Index = first
                canonical doc; `:slug` selects a specific spec/impl doc. */}
            <Route path="/docs">
              <Route index element={<Docs />} />
              <Route path=":slug" element={<Docs />} />
            </Route>

            <Route path="/agent-runs/:id/trace" element={<AgentTrace />} />

            {/* v0.2 custom visualization / board pages (CONTRACT_V2 §3.4). */}
            <Route path="/context-atlas" element={<ContextAtlas />} />
            <Route path="/package-graph" element={<PackageGraph />} />
            <Route path="/signals/triage" element={<SignalTriage />} />

            {/* Projects. */}
            <Route path="/projects">
              <Route index element={<ProjectList />} />
              <Route path="create" element={<ProjectCreate />} />
              <Route path=":id" element={<ProjectShow />} />
              <Route path=":id/control-tower" element={<ProjectControlTowerPage />} />
              <Route path=":id/edit" element={<ProjectEdit />} />
            </Route>

            {/* Companies. */}
            <Route path="/companies">
              <Route index element={<CompanyList />} />
              <Route path="create" element={<CompanyCreate />} />
              <Route path=":id" element={<CompanyShow />} />
              <Route path=":id/edit" element={<CompanyEdit />} />
            </Route>

            {/* Tasks. */}
            <Route path="/tasks">
              <Route index element={<TaskList />} />
              <Route path="create" element={<TaskCreate />} />
              <Route path=":id" element={<TaskShow />} />
              <Route path=":id/edit" element={<TaskEdit />} />
            </Route>

            {/* Users (admin). */}
            <Route path="/users">
              <Route index element={<UserList />} />
              <Route path="create" element={<UserCreate />} />
              <Route path=":id" element={<UserShow />} />
              <Route path=":id/edit" element={<UserEdit />} />
            </Route>

            {/* Roles (admin). */}
            <Route path="/roles">
              <Route index element={<RoleList />} />
              <Route path=":id" element={<RoleShow />} />
            </Route>

            {/* Permissions catalog (admin) — read-only list of the RBAC keys. */}
            <Route path="/permissions" element={<Permissions />} />

            {/* Artifacts. */}
            <Route path="/artifacts">
              <Route index element={<ArtifactList />} />
              <Route path="create" element={<ArtifactCreate />} />
              <Route path=":id" element={<ArtifactShow />} />
              <Route path=":id/edit" element={<ArtifactEdit />} />
            </Route>

            {/* Documents. */}
            <Route path="/documents">
              <Route index element={<DocumentList />} />
              <Route path="create" element={<DocumentCreate />} />
              <Route path=":id" element={<DocumentShow />} />
              <Route path=":id/edit" element={<DocumentEdit />} />
            </Route>

            {/* Evidence. */}
            <Route path="/evidence">
              <Route index element={<EvidenceList />} />
              <Route path="create" element={<EvidenceCreate />} />
              <Route path=":id" element={<EvidenceShow />} />
              <Route path=":id/edit" element={<EvidenceEdit />} />
            </Route>

            {/* Decisions. */}
            <Route path="/decisions">
              <Route index element={<DecisionList />} />
              <Route path="create" element={<DecisionCreate />} />
              <Route path=":id/room" element={<DecisionRoomRoute />} />
              <Route path=":id" element={<DecisionShow />} />
              <Route path=":id/edit" element={<DecisionEdit />} />
            </Route>

            {/* Objectives. */}
            <Route path="/objectives">
              <Route index element={<ObjectiveList />} />
              <Route path="create" element={<ObjectiveCreate />} />
              <Route path=":id" element={<ObjectiveShow />} />
              <Route path=":id/edit" element={<ObjectiveEdit />} />
            </Route>

            {/* Key results. */}
            <Route path="/key-results">
              <Route index element={<KeyResultList />} />
              <Route path="create" element={<KeyResultCreate />} />
              <Route path=":id" element={<KeyResultShow />} />
              <Route path=":id/edit" element={<KeyResultEdit />} />
            </Route>

            {/* Metrics. */}
            <Route path="/metrics">
              <Route index element={<MetricList />} />
              <Route path="create" element={<MetricCreate />} />
              <Route path=":id" element={<MetricShow />} />
              <Route path=":id/edit" element={<MetricEdit />} />
            </Route>

            {/* Measurements (append-only: list / show / create). */}
            <Route path="/measurements">
              <Route index element={<MeasurementList />} />
              <Route path="create" element={<MeasurementCreate />} />
              <Route path=":id" element={<MeasurementShow />} />
            </Route>

            {/* Skills. */}
            <Route path="/skills">
              <Route index element={<SkillList />} />
              <Route path="create" element={<SkillCreate />} />
              <Route path=":id" element={<SkillShow />} />
              <Route path=":id/workspace" element={<SkillWorkspaceRoute />} />
              <Route path=":id/edit" element={<SkillEdit />} />
            </Route>

            {/* Processes. */}
            <Route path="/processes">
              <Route index element={<ProcessList />} />
              <Route path="create" element={<ProcessCreate />} />
              <Route path=":id" element={<ProcessShow />} />
              <Route path=":id/board" element={<ContourBoardRoute />} />
              <Route path=":id/edit" element={<ProcessEdit />} />
            </Route>

            {/* Phases. */}
            <Route path="/phases">
              <Route index element={<PhaseList />} />
              <Route path="create" element={<PhaseCreate />} />
              <Route path=":id" element={<PhaseShow />} />
              <Route path=":id/edit" element={<PhaseEdit />} />
            </Route>

            {/* Rules. */}
            <Route path="/rules">
              <Route index element={<RuleList />} />
              <Route path="create" element={<RuleCreate />} />
              <Route path=":id" element={<RuleShow />} />
              <Route path=":id/edit" element={<RuleEdit />} />
            </Route>

            {/* Events (read-only). The index IS the dedicated Event Stream
                screen (frontend-display §9.1); `:id` opens the event show with
                payload + replay. The generic Refine list stays reachable at
                `/events/table` for tooling/tests (mirrors `/contexts/table`). */}
            <Route path="/events">
              <Route index element={<EventStream />} />
              <Route path="table" element={<EventList />} />
              <Route path=":id" element={<EventShow />} />
            </Route>

            {/* Rule runs (read-only). */}
            <Route path="/rule-runs">
              <Route index element={<RuleRunList />} />
              <Route path=":id" element={<RuleRunShow />} />
            </Route>

            {/* Agent runs (read-only + trace). */}
            <Route path="/agent-runs">
              <Route index element={<AgentRunList />} />
              <Route path=":id" element={<AgentRunShow />} />
            </Route>

            {/* Event sources ([PROPOSED] — list+health, read-only). */}
            <Route path="/event-sources">
              <Route index element={<EventSourceList />} />
              <Route path=":id" element={<EventSourceShow />} />
            </Route>

            {/* Effect runs ([PROPOSED] — read-only; also shown in RuleRun). */}
            <Route path="/effect-runs">
              <Route index element={<EffectRunList />} />
              <Route path=":id" element={<EffectRunShow />} />
            </Route>

            {/* Transition proposals ([PROPOSED] — the human approve/reject gate). */}
            <Route path="/transition-proposals">
              <Route index element={<TransitionProposalList />} />
              <Route path=":id" element={<TransitionProposalShow />} />
            </Route>

            {/* Contour instances ([PROPOSED] — live contour run, read-only). */}
            <Route path="/contour-instances">
              <Route index element={<ContourInstanceList />} />
              <Route path=":id" element={<ContourInstanceShow />} />
            </Route>

            <Route path="/action-cards">
              <Route index element={<ActionCardList />} />
              <Route path=":id" element={<ActionCardShow />} />
            </Route>

            {/* ===== v0.2: Context Atlas, Signals, Packages, Claims, etc. ===== */}

            {/* Contexts. The list view IS the Context Atlas board; `:id` opens
                the rich Context Detail (+ packet preview) rather than the
                generic show. */}
            <Route path="/contexts">
              <Route index element={<ContextAtlas />} />
              <Route path="create" element={<ContextCreate />} />
              <Route path=":id" element={<ContextDetail />} />
              <Route path=":id/control-room" element={<ContextControlRoomRoute />} />
              <Route path=":id/explorer" element={<ContextExplorerRoute />} />
              <Route path=":id/strategy-map" element={<StrategyMapRoute />} />
              <Route path=":id/edit" element={<ContextEdit />} />
              {/* Generic Refine list/show kept reachable for tooling/tests. */}
              <Route path="table" element={<ContextList />} />
              <Route path=":id/details" element={<ContextShow />} />
            </Route>

            {/* Context bindings. */}
            <Route path="/context-bindings">
              <Route index element={<ContextBindingList />} />
              <Route path="create" element={<ContextBindingCreate />} />
              <Route path=":id" element={<ContextBindingShow />} />
              <Route path=":id/edit" element={<ContextBindingEdit />} />
            </Route>

            {/* Signals. */}
            <Route path="/signals">
              <Route index element={<SignalList />} />
              <Route path="create" element={<SignalCreate />} />
              <Route path=":id" element={<SignalShow />} />
              <Route path=":id/edit" element={<SignalEdit />} />
            </Route>

            {/* Packages (unified store for all five package types). */}
            <Route path="/packages">
              <Route index element={<PackageList />} />
              <Route path="create" element={<PackageCreate />} />
              <Route path=":id" element={<PackageShow />} />
              <Route path=":id/edit" element={<PackageEdit />} />
            </Route>

            {/* Package links (append-only: list / show / create, no edit). */}
            <Route path="/package-links">
              <Route index element={<PackageLinkList />} />
              <Route path="create" element={<PackageLinkCreate />} />
              <Route path=":id" element={<PackageLinkShow />} />
            </Route>

            {/* Bets — type-scoped facade over `packages` (package_type=bet). */}
            <Route path="/bets">
              <Route index element={<BetList />} />
              <Route path="create" element={<BetCreate />} />
              <Route path=":id" element={<BetShow />} />
              <Route path=":id/edit" element={<BetEdit />} />
            </Route>

            {/* Frames — facade over `packages` (package_type=frame). */}
            <Route path="/frames">
              <Route index element={<FrameList />} />
              <Route path="create" element={<FrameCreate />} />
              <Route path=":id" element={<FrameShow />} />
              <Route path=":id/edit" element={<FrameEdit />} />
            </Route>

            {/* Principles — facade over `packages` (package_type=principle). */}
            <Route path="/principles">
              <Route index element={<PrincipleList />} />
              <Route path="create" element={<PrincipleCreate />} />
              <Route path=":id" element={<PrincipleShow />} />
              <Route path=":id/edit" element={<PrincipleEdit />} />
            </Route>

            {/* Claims. */}
            <Route path="/claims">
              <Route index element={<ClaimList />} />
              <Route path="create" element={<ClaimCreate />} />
              <Route path=":id" element={<ClaimShow />} />
              <Route path=":id/edit" element={<ClaimEdit />} />
            </Route>

            {/* Artifact positions. */}
            <Route path="/artifact-positions">
              <Route index element={<ArtifactPositionList />} />
              <Route path="create" element={<ArtifactPositionCreate />} />
              <Route path=":id" element={<ArtifactPositionShow />} />
              <Route path=":id/edit" element={<ArtifactPositionEdit />} />
            </Route>

            {/* Timers. */}
            <Route path="/timers">
              <Route index element={<TimerList />} />
              <Route path="create" element={<TimerCreate />} />
              <Route path=":id" element={<TimerShow />} />
              <Route path=":id/edit" element={<TimerEdit />} />
            </Route>

            {/* ===== S13 (Wave 4a): human surfaces (append-only block). =====
                Standalone custom routes (not Refine CRUD): the Approval Center,
                the Runtime Trace Viewer (keyed by agent run), and the Context
                Packet Inspector (keyed by context + ?lens_id). Registered as
                top-level paths so this slice's block stays self-contained and
                does not reorder the existing resource route groups. */}
            <Route path="/approvals" element={<ApprovalCenter />} />
            <Route path="/agent-runs/:id/trace-viewer" element={<TraceViewerRoute />} />
            <Route path="/contexts/:id/packet" element={<PacketInspectorRoute />} />

            {/* ===== S14 (Wave 4b): ten read-projection surfaces (append-only).
                The id-keyed drills (Control Tower, Context Explorer, Strategy
                Map, Skill Workspace, Contour Board) are nested under their
                owning resource blocks above. The standalone surfaces below are
                top-level: the Actor Passport (keyed by actor id), the Access
                Lens (actor-scoped access explainer; index renders a "choose an
                actor" empty state), and the three list-first homes (Attention
                Queue, Lens Builder catalog + detail, Package Catalog). ===== */}
            <Route path="/actors/:id/passport" element={<ActorPassportRoute />} />
            <Route path="/access-lens" element={<AccessLensRoute />} />
            <Route path="/access-lens/:actorId" element={<AccessLensRoute />} />
            <Route path="/attention-queue" element={<AttentionQueue />} />
            <Route path="/lens-builder">
              <Route index element={<LensBuilderList />} />
              <Route path=":id" element={<LensBuilderRoute />} />
            </Route>
            <Route path="/package-catalog" element={<PackageCatalogPage />} />

            {/* ===== S05/S18 (Wave 4c): executive + knowledge surfaces
                (append-only). The Executive Cockpit is the founder portfolio
                landing; the Knowledge Assistant is the access-filtered retrieval
                workbench. Top-level custom routes — not Refine CRUD. ===== */}
            <Route path="/executive" element={<ExecutiveCockpit />} />
            <Route path="/knowledge/assistant" element={<KnowledgeAssistant />} />

            {/* Catch-all 404 inside the shell. */}
            <Route path="*" element={<ErrorComponent />} />
          </Route>

          {/* Unauthenticated login: redirect to home if already signed in. */}
          <Route
            element={
              <Authenticated key="login-redirect" fallback={<Outlet />}>
                <NavigateToResource resource="projects" />
              </Authenticated>
            }
          >
            <Route path="/login" element={<Login />} />
          </Route>
        </Routes>

        <UnsavedChangesNotifier />
        <DocumentTitleHandler />
      </Refine>
    </ErrorBoundary>
  );
}
